/**
 * The entity picker the three Finance screens share.
 *
 * One LLC at a time: every Finance query is scoped to an entity, so the choice lives in the URL
 * (`?entity=<id>`) where it survives a reload and travels with a pasted link. With no `entity` in
 * the URL the first entity is chosen, by writing it into the URL rather than holding it in state —
 * so the page and the picker read the same value from the same place.
 *
 * The counts beside each name come from `GET /api/entities?counts=finance`, computed in SQL in the
 * same statement as the list. They are displayed, never added up here.
 */

import { useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Entity, type EntityFinanceCounts } from '../api.js';

export const ENTITY_PARAM = 'entity';

/** The entity list with its finance counts. One query key, shared by the picker and the pages. */
export function useEntities() {
  return useQuery({
    queryKey: ['entities', 'finance'],
    queryFn: () => api.get<{ entities: Entity[] }>('/api/entities?counts=finance'),
  });
}

/** The selected entity id from the URL, or null until the picker has chosen the default. */
export function useEntityId(): string | null {
  const [params] = useSearchParams();
  return params.get(ENTITY_PARAM);
}

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/** `3 unclassified · 2 rule hits · 1 AI proposal · 0 anomalies`, plus conflicts when there are any. */
export function describeCounts(f: EntityFinanceCounts): string {
  const parts = [
    `${f.unclassified} unclassified`,
    plural(f.rule_proposals, 'rule hit'),
    plural(f.pending_ai_proposals, 'AI proposal'),
    plural(f.unresolved_anomalies, 'anomaly', 'anomalies'),
  ];
  if (f.rule_conflicts > 0) parts.push(plural(f.rule_conflicts, 'rule conflict'));
  return parts.join(' · ');
}

export function EntityPicker(): ReactNode {
  const [params, setParams] = useSearchParams();
  const entities = useEntities();
  const selected = params.get(ENTITY_PARAM);
  const first = entities.data?.entities[0];

  // Default to the first entity by writing it into the URL. `replace`, so Back does not step
  // through a URL the person never typed.
  useEffect(() => {
    if (selected === null && first) {
      setParams(
        (prev) => {
          prev.set(ENTITY_PARAM, first.id);
          return prev;
        },
        { replace: true },
      );
    }
  }, [selected, first, setParams]);

  if (entities.isError) return <p className="error">Could not load entities.</p>;

  return (
    <label className="entity-picker">
      Entity
      <select
        value={selected ?? ''}
        disabled={!entities.data}
        onChange={(e) =>
          setParams((prev) => {
            prev.set(ENTITY_PARAM, e.target.value);
            return prev;
          })
        }
      >
        {!entities.data && <option value="">Loading…</option>}
        {entities.data?.entities.length === 0 && <option value="">No entities</option>}
        {(entities.data?.entities ?? []).map((entity) => (
          <option key={entity.id} value={entity.id}>
            {entity.name}
            {entity.finance ? ` — ${describeCounts(entity.finance)}` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}
